import { useAppRouteParams } from "@/AppRoutes";
import { TadSheet, useTadSheetsQuery } from "@/domain/TadSheet";
import { ComplexFirestoreQuerySetting } from "@/firebase/firestore";
import { useEffect, useMemo } from "react";
import { usePermissions } from "../../InOrganizationProvider/PermissionsProvider";

// 読める TADS の一覧を購読する。権限が確定するまでは購読しない。
// 広域の `tad_sheets:read` があれば全件を購読する。
//
// `includeParametric` を指定すると、広域の read が無い人でも `tad_sheet:read:<id>` を持つ TADS だけを
// ID で絞って購読する (useAuthorizedSheets と同じ方式)。
export const useTadSheetsWithPermission = ({
  includeParametric = false,
}: { includeParametric?: boolean } = {}): {
  tadSheets: TadSheet[];
  isLoadedTadSheets: boolean;
  canRead: boolean;
} => {
  const { organizationId } = useAppRouteParams();
  const { me, hasPermission, isLoadedMe } = usePermissions();
  const canReadAll = hasPermission("tad_sheets", "read");

  // パラメトリックな read を持つ TADS の ID (広域の read があれば使わない)
  const parametricIds = useMemo(
    () =>
      includeParametric && !canReadAll
        ? [...new Set(me?.permissions.tad_sheet.read ?? [])].sort()
        : [],
    [includeParametric, canReadAll, me]
  );
  const parametricIdsKey = parametricIds.join(",");

  const query = useMemo<ComplexFirestoreQuerySetting | null>(
    () =>
      canReadAll
        ? null
        : {
            logicalOp: "or",
            ops: parametricIds.map((id) => ({
              // NOTE: "__name__" は documentId() が返す値 (useAuthorizedSheets と同じ)
              fieldPath: "__name__",
              opStr: "==" as const,
              value: id,
            })),
          },
    // parametricIds は parametricIdsKey と 1:1 なので key で判定する
    [canReadAll, parametricIdsKey]
  );

  // 読める TADS が 1 件も無いのに購読すると、空の OR (= 全件) で購読してしまうので止める
  const canRead = canReadAll || parametricIds.length > 0;
  const enabled = isLoadedMe && canRead;

  const [tadSheets, isLoadedTadSheets, , changeQuerySetting] =
    useTadSheetsQuery(
      { organizationId },
      query === null ? undefined : async () => query,
      { enabled }
    );

  useEffect(() => {
    // enabled が false のうちに changeQuerySetting を呼ぶと、enabled が true になった瞬間に
    // stale なクエリで購読されてしまうため、enabled が true のときだけ反映する (useAuthorizedSheets と同じ)。
    if (!enabled) return;
    changeQuerySetting(query);
  }, [query, enabled]);

  return {
    tadSheets,
    isLoadedTadSheets: isLoadedMe && isLoadedTadSheets,
    canRead: !isLoadedMe || canRead,
  };
};
